import { useState, useEffect } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";

const inputCls = "w-full bg-transparent border-2 border-[#F2EFEB] h-11 px-3 focus:outline-none focus:border-[#DFFF00] text-sm";

// Convert a stored ISO/UTC string into the value a datetime-local input expects (local time).
function isoToLocalInput(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d)) return "";
  const off = d.getTimezoneOffset();
  return new Date(d.getTime() - off * 60000).toISOString().slice(0, 16);
}

export default function DropCountdownManager() {
  const [form, setForm] = useState({ enabled: false, drop_at: "", headline: "The drop is coming" });
  const [loaded, setLoaded] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/admin/drop-countdown")
      .then(({ data }) => setForm({
        enabled: data.enabled,
        drop_at: isoToLocalInput(data.drop_at),
        headline: data.headline || "The drop is coming",
      }))
      .finally(() => setLoaded(true));
  }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async (e) => {
    e.preventDefault();
    if (form.enabled && !form.drop_at) return toast.error("Set a drop date/time to turn the countdown on.");
    setSaving(true);
    try {
      const { data } = await api.put("/admin/drop-countdown", {
        enabled: form.enabled,
        drop_at: form.drop_at ? new Date(form.drop_at).toISOString() : null,
        headline: form.headline,
      });
      setForm({ enabled: data.enabled, drop_at: isoToLocalInput(data.drop_at), headline: data.headline || "The drop is coming" });
      toast.success(data.enabled ? "Countdown is live on the site" : "Countdown is off");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const turnOff = async () => {
    setSaving(true);
    try {
      await api.put("/admin/drop-countdown", { enabled: false, drop_at: null, headline: form.headline });
      setForm((f) => ({ ...f, enabled: false }));
      toast.success("Countdown turned off");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  if (!loaded) return <p className="opacity-60 py-8 animate-pulse">Loading...</p>;

  const future = form.drop_at && new Date(form.drop_at) > new Date();

  return (
    <form onSubmit={save} data-testid="drop-countdown-form" className="max-w-xl border-2 border-[#F2EFEB]/40 p-6">
      <p className="overline text-[#DFFF00] mb-2">Drop countdown banner</p>
      <p className="font-mono-ui text-[0.65rem] opacity-60 mb-5">Shows a live countdown on the storefront. It only appears while a future drop date is set here, and hides itself automatically once the date passes.</p>

      <label className="flex items-center gap-2 text-sm cursor-pointer mb-4">
        <input type="checkbox" checked={form.enabled} onChange={(e) => set("enabled", e.target.checked)} data-testid="drop-enabled-input" />
        Show the countdown banner on the site
      </label>

      <label className="overline block mb-1">Drop date &amp; time (your local time)</label>
      <input type="datetime-local" className={inputCls} value={form.drop_at} onChange={(e) => set("drop_at", e.target.value)} data-testid="drop-datetime-input" />
      {form.drop_at && !future && <p className="font-mono-ui text-xs text-[#FF2A00] mt-1">That time is in the past — pick a future date.</p>}

      <label className="overline block mb-1 mt-4">Headline</label>
      <input className={inputCls} value={form.headline} onChange={(e) => set("headline", e.target.value)} data-testid="drop-headline-input" maxLength={60} placeholder="DROP 01 — FAUCET COLLECTIVE" />

      <div className="flex gap-3 mt-6">
        <button type="submit" disabled={saving} data-testid="drop-save-btn" className="h-12 px-6 border-2 border-[#F2EFEB] bg-[#F2EFEB] text-[#090909] uppercase tracking-widest text-sm hover:bg-[#DFFF00] hover:border-[#DFFF00] transition-colors disabled:opacity-60">
          {saving ? "Saving..." : "Save countdown →"}
        </button>
        {form.enabled && (
          <button type="button" onClick={turnOff} disabled={saving} className="h-12 px-5 border-2 border-[#FF2A00] text-[#FF2A00] uppercase text-xs tracking-widest hover:bg-[#FF2A00] hover:text-[#090909] transition-colors">Turn off</button>
        )}
      </div>
    </form>
  );
}
